import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import styles from "./styles.module.scss";
import { getSettlementRequests } from "../../../../setup/api/services/contract";
import { ISettlementRequest } from "../../../../setup/api/services/contract/models";
import Spinner from "../../../../components/common/Spinner";

const PENDING_STATUSES = ["PENDING_OWNER_APPROVAL"];
const ACCEPTED_STATUSES = ["ACCEPTED", "APPROVED", "EXECUTED", "COMPLETED"];
const REJECTED_STATUSES = ["REJECTED", "CANCELLED", "DENIED"];

interface SettlementRequestsProps {
  complexContractId: string;
}

/**
 * Settlement requests raised against a complex contract, ported from the
 * business portal. Accepting or rejecting one is the organization owner's
 * decision, so the admin sees each request's state without acting on it.
 */
const SettlementRequests = ({ complexContractId }: SettlementRequestsProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [requests, setRequests] = useState<ISettlementRequest[]>([]);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getSettlementRequests(complexContractId);
      const map = res?.data ?? {};
      const flat = Object.entries(map).flatMap(([sourceContractId, arr]) =>
        Array.isArray(arr)
          ? arr.map((req) => ({ ...req, sourceContractId }))
          : []
      );
      setRequests(flat);
    } catch {
      setRequests([]);
    } finally {
      setLoading(false);
    }
  }, [complexContractId]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  if (loading) {
    return (
      <div className={styles.loadingRow}>
        <Spinner />
      </div>
    );
  }

  if (requests.length === 0) return null;

  return (
    <div className={styles.container}>
      <h2 className={styles.sectionTitle}>{t("lbl_settlement_requests")}</h2>
      <div className={styles.list}>
        {requests?.map((req) => {
          const formattedDate = req.createdAt
            ? new Date(req.createdAt).toLocaleDateString("ar-EG", {
                day: "numeric",
                month: "long",
                year: "numeric",
              })
            : "";

          return (
            <div key={req.id} className={styles.item}>
              <div className={styles.itemRight}>
                <p
                  className={styles.itemTitleLink}
                  onClick={() =>
                    navigate(`/contract/${req.destinationContractId}`)
                  }>
                  {req.contractTitle || req.destinationContractShortId}
                </p>
                <p className={styles.itemDate}>{formattedDate}</p>
              </div>
              <div className={styles.itemLeft}>
                <div className={styles.amountRow}>
                  <span className={styles.amount}>
                    {req.amount?.toLocaleString()}
                  </span>
                  <span className={styles.currency}>
                    {t("lbl_saudi_currency")}
                  </span>
                </div>
                {ACCEPTED_STATUSES.includes(req.status) && (
                  <span className={styles.badgeAccepted}>
                    {t("lbl_settlement_accepted")}
                  </span>
                )}
                {REJECTED_STATUSES.includes(req.status) && (
                  <span className={styles.badgeRejected}>
                    {t("lbl_settlement_rejected")}
                  </span>
                )}
                {PENDING_STATUSES.includes(req.status) && (
                  <span className={styles.badgePending}>
                    {t("lbl_settlement_pending")}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SettlementRequests;
